import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Clock, Star, TrendingUp, Wand2, Command, Plug } from 'lucide-react';
import { useApp } from '@/store/app';
import { TOOLS, TOOLS_BY_CATEGORY } from '@/registry';
import { CATEGORIES } from '@/registry/categories';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ToolCard } from '@/components/ToolCard';
import { formatRelativeTime } from '@/lib/utils';
import { cn } from '@/lib/cn';
import type { Tool } from '@/registry/types';

export function HomeView() {
  const { recents, favorites, togglePalette, toggleAgent } = useApp();
  const navigate = useNavigate();

  const recentTools = recents
    .map((r) => TOOLS.find((t) => t.id === r.toolId))
    .filter((t): t is Tool => Boolean(t))
    .slice(0, 6);

  const featured = TOOLS.filter((t) => t.featured).slice(0, 8);

  return (
    <div className="relative">
      {/* Sticker hero — halftone backdrop, display headline, decorative stamps */}
      <div className="relative overflow-hidden border-b-2 border-fg/85 dark:border-fg/30 bg-gradient-to-b from-amber-100/60 via-orange-50/40 to-bg dark:from-amber-500/10 dark:via-orange-500/5 dark:to-bg">
        <div className="absolute inset-0 halftone-warm pointer-events-none opacity-80" />

        {/* Sun sticker — top right (smaller + tucked further off-screen on mobile) */}
        <svg
          className="absolute -top-10 -right-10 size-28 sm:-top-6 sm:-right-6 sm:size-44 md:size-56 pointer-events-none animate-bob opacity-95"
          viewBox="0 0 200 200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g stroke="hsl(24 30% 14%)" strokeWidth="3.5" strokeLinecap="round">
            {/* rays */}
            {Array.from({ length: 12 }).map((_, i) => {
              const a = (i * Math.PI) / 6;
              const x1 = 100 + Math.cos(a) * 70;
              const y1 = 100 + Math.sin(a) * 70;
              const x2 = 100 + Math.cos(a) * 92;
              const y2 = 100 + Math.sin(a) * 92;
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
            })}
          </g>
          <circle cx="100" cy="100" r="56" fill="hsl(38 100% 60%)" stroke="hsl(24 30% 14%)" strokeWidth="3.5" />
          <circle cx="86" cy="92" r="4" fill="hsl(24 30% 14%)" />
          <circle cx="114" cy="92" r="4" fill="hsl(24 30% 14%)" />
          <path d="M 84 112 Q 100 124 116 112" stroke="hsl(24 30% 14%)" strokeWidth="3.5" strokeLinecap="round" fill="none" />
        </svg>

        {/* Star burst — left of headline */}
        <svg
          className="absolute top-20 left-4 size-12 pointer-events-none hidden md:block"
          viewBox="0 0 100 100"
        >
          <path
            d="M50 5 L58 38 L92 42 L64 60 L74 92 L50 72 L26 92 L36 60 L8 42 L42 38 Z"
            fill="hsl(28 95% 54%)"
            stroke="hsl(24 30% 14%)"
            strokeWidth="3"
            strokeLinejoin="round"
          />
        </svg>

        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 md:px-8 py-10 sm:py-14 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Stamp pill */}
            <div className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 mb-4 sm:mb-5 text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded-full border-2 border-stone-900 bg-amber-300 text-stone-900 shadow-sticker-sm rotate-[-1.5deg] max-w-full">
              <span className="size-1.5 rounded-full bg-stone-900 animate-pulse shrink-0" />
              <span className="truncate">{TOOLS.length}+ utilities · runs locally · privacy-first</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight max-w-3xl leading-[1.02]">
              Your work,{' '}
              <span className="scribble-under text-accent">simplified.</span>
            </h1>
            <p className="text-sm sm:text-base text-fg-muted mt-3 sm:mt-4 max-w-2xl leading-relaxed font-medium">
              The Swiss Army knife for everyday tasks — PDFs, images, CSVs, calculators, codes,
              colors. Search by intent, drop any file, or chain steps into a workflow.
            </p>

            <div className="mt-6 sm:mt-7 flex flex-wrap items-center gap-2 sm:gap-3">
              <Button variant="primary" size="lg" onClick={togglePalette} icon={<Command size={14} />}>
                Open command bar
                <kbd className="ml-2 bg-fg/15 border-fg/20 text-accent-fg hidden sm:inline-flex">⌘K</kbd>
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={toggleAgent}
                icon={<Sparkles size={14} className="text-orange-500 dark:text-amber-400" />}
              >
                Ask the agent
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => navigate('/workflows')}
                icon={<Wand2 size={14} />}
              >
                Build a workflow
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-8 py-8 md:py-10 space-y-10 md:space-y-12">
        {/* Recent + Favorites rail */}
        {(recentTools.length > 0 || favorites.length > 0) && (
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {recentTools.length > 0 && (
              <Card padded className="!p-0 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-fg-subtle" />
                    <h2 className="text-sm font-semibold">Recent</h2>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/recent')}>
                    All <ArrowRight size={12} />
                  </Button>
                </div>
                <div className="p-2">
                  {recentTools.slice(0, 4).map((tool, i) => (
                    <button
                      key={tool.id}
                      onClick={() => navigate(`/tool/${tool.id}`)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover transition"
                    >
                      <span
                        className={cn(
                          'size-8 rounded-md flex items-center justify-center bg-gradient-to-br',
                          CATEGORIES.find((c) => c.id === tool.category)?.gradient,
                        )}
                      >
                        <tool.icon size={14} className="text-white" />
                      </span>
                      <div className="flex-1 text-left min-w-0">
                        <div className="text-sm font-medium truncate">{tool.name}</div>
                        <div className="text-xs text-fg-muted truncate">
                          {formatRelativeTime(recents[i]?.ts ?? Date.now())}
                        </div>
                      </div>
                      <ArrowRight size={12} className="text-fg-subtle shrink-0" />
                    </button>
                  ))}
                </div>
              </Card>
            )}
            {favorites.length > 0 && (
              <Card padded className="!p-0 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Star size={14} className="text-warning fill-warning" />
                    <h2 className="text-sm font-semibold">Favorites</h2>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/favorites')}>
                    All <ArrowRight size={12} />
                  </Button>
                </div>
                <div className="p-2">
                  {favorites
                    .slice(0, 4)
                    .map((id) => TOOLS.find((t) => t.id === id))
                    .filter((t): t is Tool => Boolean(t))
                    .map((tool) => (
                      <button
                        key={tool.id}
                        onClick={() => navigate(`/tool/${tool.id}`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover transition"
                      >
                        <span
                          className={cn(
                            'size-8 rounded-md flex items-center justify-center bg-gradient-to-br',
                            CATEGORIES.find((c) => c.id === tool.category)?.gradient,
                          )}
                        >
                          <tool.icon size={14} className="text-white" />
                        </span>
                        <div className="flex-1 text-left min-w-0">
                          <div className="text-sm font-medium truncate">{tool.name}</div>
                          <div className="text-xs text-fg-muted truncate">{tool.description}</div>
                        </div>
                        <ArrowRight size={12} className="text-fg-subtle shrink-0" />
                      </button>
                    ))}
                </div>
              </Card>
            )}
          </section>
        )}

        {/* Featured */}
        {featured.length > 0 && (
          <section>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-fg-subtle" />
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-subtle">
                    Featured today
                  </h2>
                </div>
                <h3 className="text-2xl font-semibold tracking-tight mt-1">
                  Most used by your team
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {featured.map((tool) => (
                <ToolCard key={tool.id} tool={tool} />
              ))}
            </div>
          </section>
        )}

        {/* All categories */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-subtle">
              Browse by category
            </h2>
            <span className="text-xs text-fg-subtle">{CATEGORIES.length} categories</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CATEGORIES.map((cat, i) => {
              const Icon = cat.icon;
              const count = TOOLS_BY_CATEGORY[cat.id]?.length ?? 0;
              return (
                <motion.button
                  key={cat.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  onClick={() => navigate(`/c/${cat.id}`)}
                  className="group relative overflow-hidden text-left rounded-xl border border-border bg-surface hover:border-border-strong hover:-translate-y-0.5 transition-all p-4"
                >
                  <div
                    className={cn(
                      'absolute -top-12 -right-12 size-32 rounded-full opacity-20 blur-2xl transition-opacity group-hover:opacity-40 bg-gradient-to-br',
                      cat.gradient,
                    )}
                  />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className={cn(
                          'size-9 rounded-lg flex items-center justify-center bg-gradient-to-br shadow-sm',
                          cat.gradient,
                        )}
                      >
                        <Icon size={16} className="text-white" />
                      </div>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                    <h3 className="text-sm font-semibold mb-1">{cat.name}</h3>
                    <p className="text-xs text-fg-muted leading-relaxed">{cat.description}</p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </section>

        {/* Trust markers — inline, non-interactive */}
        <section className="pt-6 border-t border-dashed border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5">
            {[
              {
                icon: Sparkles,
                title: 'Intent-first',
                text: 'Type what you want — the agent picks the right utility.',
              },
              {
                icon: Plug,
                title: 'Composable',
                text: 'Chain tools into reusable workflows. Save them. Share them.',
              },
              {
                icon: Star,
                title: 'Privacy native',
                text: 'Files never leave your browser. No accounts required.',
              },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className="size-7 rounded-md bg-accent-soft text-accent flex items-center justify-center shrink-0">
                    <Icon size={13} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight">{f.title}</div>
                    <div className="text-xs text-fg-muted leading-relaxed mt-1">{f.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
